<template>
  <div class="join-container">
    <div
      class="d-flex flex-row flex-nowrap align-items-center justify-content-between"
    >
      <div class="col text-center">
        <img
          class="mx-auto d-block pt-4 logo-image"
          v-if="studio.logo"
          :src="studio.logo.thumbnail"
        />
        <div v-if="!showExtraFields">
          <div class="h1 pt-md-4 py-2">{{ $t('STUDIO.TITLE.JOIN') }}</div>
          <div class="pt-md-2">
            {{ $t('STUDIO.TEXT.WE_WOULD_LOVE_IT') }}
          </div>
          <HMButton
            v-if="isLoggedIn"
            color="brand"
            class="my-md-4 my-3"
            :loading="isLoading"
            @click="goToExtraFields()"
            >{{ $t('STUDIO.BUTTON.JOIN_STUDIO') }}
          </HMButton>
          <p v-else>
            {{ $t('GENERAL.TEXT.LOG_IN_TO_CONTINUE') }}
          </p>
        </div>
      </div>
    </div>
    <div v-if="showExtraFields">
      <div class="row justify-content-center">
        <div class="col-3 text-center">
          <h1 class="my-2">{{ $t('STUDIO.TITLE.FIRST_STEPS') }}</h1>
          <p class="my-4">{{ $t('STUDIO.TEXT.EXTRA_DETAILS') }}</p>
        </div>
      </div>
      <ValidationObserver ref="form" v-slot="{ invalid }">
        <form v-if="signUpFields" @submit.prevent="registerStudent">
          <div
            class="row justify-content-center"
            v-for="field in signUpFields"
            :key="field.label"
          >
            <div class="col-3">
              <HMTextInput
                :label="$t(field.label)"
                v-model="extraFieldsModel[field.model]"
                :rules="field.required ? 'required' : ''"
                v-if="fieldVisible(field.data, field.displayed)"
              ></HMTextInput>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col text-center">
              <HMButton
                type="submit"
                color="brand"
                :disabled="invalid"
                :loading="isLoading"
                >{{ $t('STUDIO.BUTTON.CONTINUE_TO_STUDIO') }}</HMButton
              >
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import HMButton from '@/web/components/common/HMButton';
import HMTextInput from '@/web/components/common/HMTextInput';
import { errorToMessage } from '@/core/utils/errors';
import { ValidationObserver } from 'vee-validate';
import Login from '@/web/components/signup/Login';
import { getStudioURL } from '../../router/custom-domain-utils';

export default Vue.extend({
  name: 'StudioJoin',
  components: { ValidationObserver },
  data() {
    return {
      showExtraFields: false,
      extraFieldsModel: {},
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
      user: 'user/user',
      isLoggedIn: 'user/isLoggedIn',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    signUpFields() {
      return [
        {
          label: 'STUDIO.LABELS.FIRST_NAME',
          model: 'first_name',
          data: this.user.student.first_name,
          displayed: true,
          required: true,
        },
        {
          label: 'STUDIO.LABELS.LAST_NAME',
          model: 'last_name',
          data: this.user.student.last_name,
          displayed: true,
          required: true,
        },
        {
          label: 'STUDIO.LABELS.PHONE',
          model: 'phone',
          data: this.user.student.phone,
          displayed: this.studio.phone_number_displayed,
          required: this.studio.phone_number_required,
        },
        {
          label: 'STUDIO.LABELS.ADDRESS',
          model: 'address',
          data: this.user.student.address,
          displayed: this.studio.address_displayed,
          required: this.studio.address_required,
        },
        {
          label: 'STUDIO.LABELS.CITY',
          model: 'city',
          data: this.user.student.city,
          displayed: this.studio.city_displayed,
          required: this.studio.city_required,
        },
        {
          label: 'STUDIO.LABELS.ZIPCODE',
          model: 'zipcode',
          data: this.user.student.zipcode,
          displayed: this.studio.zip_displayed,
          required: this.studio.zip_required,
        },
        {
          label: 'STUDIO.LABELS.STATE_PROVINCE',
          model: 'state',
          data: this.user.student.state,
          displayed: this.studio.state_displayed,
          required: this.studio.state_required,
        },
        {
          label: 'STUDIO.LABELS.COUNTRY',
          model: 'country',
          data: this.user.student.country,
          displayed: this.studio.country_displayed,
          required: this.studio.country_required,
        },
      ];
    },
  },
  methods: {
    fieldVisible(field, displayed) {
      return displayed ? !field : false;
    },
    goToExtraFields() {
      this.showExtraFields = this.signUpFields.some((data, displayed) =>
        this.fieldVisible(data, displayed)
      );
      if (this.showExtraFields) return null;
      this.joinStudio();
    },
    async registerStudent() {
      this.isLoading = true;
      const data = { student: this.extraFieldsModel };
      try {
        await this.$store.dispatch('user/updateStudent', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
        this.isLoading = false;
      }
      await this.joinStudio();
    },
    async joinStudio() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('studio/joinStudio', this.studioURL);
        this.$router.push({ name: 'studio-home' });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
      await this.$store.dispatch('studio/getStudio', this.studioURL);
      this.isLoading = false;
    },
  },
  mounted() {
    this.$store.dispatch('user/meStudent');
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.logo-image {
  max-width: 100%;
  padding: 0 1rem 1rem 1rem;
  max-height: 150px;
}
.join-container {
  padding-top: 8rem;
}
</style>
